import { useCallback, useEffect, useRef } from 'react'

/**
 * A custom hook that manages restoring scroll position when navigating between journeys.
 *
 * @param nextJourneyId - The ID of the next journey to navigate to.
 * @returns A ref to the journey content element
 */
export const useJourneyScrollPosition = (nextJourneyId?: string) => {
  // Store a reference to the journey content element
  const elementRef = useRef<HTMLElement>(null)

  // Store the current journey ID
  const currentJourneyRef = useRef<string | undefined>()

  // Store scroll position of each journey
  const journeyToScrollPositionRef = useRef<Record<string, number>>({})

  // Function to clear all stored scroll positions
  const clearScrollPosition = useCallback(() => {
    journeyToScrollPositionRef.current = {}
  }, [])

  // Store the scroll position of the current journey
  const onScroll = (event: Event) => {
    if (currentJourneyRef.current != null) {
      journeyToScrollPositionRef.current[currentJourneyRef.current] = (event.target as HTMLElement)?.scrollTop ?? 0
    }
  }

  useEffect(() => {
    const currentJourney = nextJourneyId
    const lastJourney = currentJourneyRef.current

    // Update to the latest journey
    currentJourneyRef.current = nextJourneyId

    // If we're not going to any journey, we don't need to do anything
    if (lastJourney == null && currentJourney == null) {
      currentJourneyRef.current = undefined
      elementRef.current?.removeEventListener('scroll', onScroll)
      return
    }

    // If nextJourneyId becomes null/undefined after having a value, it likely means the journey was canceled
    // Clear all scroll positions in this case
    if (lastJourney != null && currentJourney == null) {
      clearScrollPosition()
      currentJourneyRef.current = undefined
      elementRef.current?.removeEventListener('scroll', onScroll)
      return
    }

    // If current journey is not null and we have a scroll position for it, we know that we've seen this journey before
    const hasVisitedJourneyBefore = currentJourney != null && journeyToScrollPositionRef.current[currentJourney] != null

    if (hasVisitedJourneyBefore) {
      // Restore to the last known scroll position of the journey
      elementRef.current?.scrollTo({ top: journeyToScrollPositionRef.current[currentJourney] })
    } else {
      // If we've never seen this journey before, scroll to the top
      elementRef.current?.scrollTo({ top: 0 })
    }

    // Listen for scroll events on the journey content
    const currentElement = elementRef.current
    currentElement?.addEventListener('scroll', onScroll)
    return () => {
      currentElement?.removeEventListener('scroll', onScroll)
    }
  }, [nextJourneyId, clearScrollPosition])

  return {
    ref: elementRef,
  }
}
